/**
 * Validation functions for Prompt Settings
 * 
 * Provides validation for all prompt configuration fields
 * including inference configuration settings.
 * 
 * _Requirements: 3.2, 3.4, 4.2, 4.4, 5.2, 5.4, 9.1_
 */

import { VALIDATION_RULES } from './promptConstants';

/**
 * Validate temperature value (0.0 - 1.0 range)
 * @param {any} value - The value to validate
 * @returns {{ isValid: boolean, error: string | null }}
 * 
 * _Requirements: 3.2, 3.4_
 */
export function validateTemperature(value) {
  const num = parseFloat(value);
  
  if (isNaN(num)) {
    return { isValid: false, error: VALIDATION_RULES.temperature.errorMessage };
  }
  
  if (num < VALIDATION_RULES.temperature.min || num > VALIDATION_RULES.temperature.max) {
    return { isValid: false, error: VALIDATION_RULES.temperature.errorMessage };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate topP value (0.0 - 1.0 range)
 * @param {any} value - The value to validate
 * @returns {{ isValid: boolean, error: string | null }}
 * 
 * _Requirements: 4.2, 4.4_
 */
export function validateTopP(value) {
  const num = parseFloat(value);
  
  if (isNaN(num)) {
    return { isValid: false, error: VALIDATION_RULES.topP.errorMessage };
  }
  
  if (num < VALIDATION_RULES.topP.min || num > VALIDATION_RULES.topP.max) {
    return { isValid: false, error: VALIDATION_RULES.topP.errorMessage };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate maxTokens value (positive integer)
 * @param {any} value - The value to validate
 * @returns {{ isValid: boolean, error: string | null }}
 * 
 * _Requirements: 5.2, 5.4_
 */
export function validateMaxTokens(value) {
  const num = parseInt(value, 10);
  
  // Check if it's a valid integer
  if (isNaN(num) || !Number.isInteger(Number(value)) || num !== Number(value)) {
    // Allow string integers like "100"
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
      const parsed = parseInt(value.trim(), 10);
      if (parsed >= VALIDATION_RULES.maxTokens.min && parsed <= VALIDATION_RULES.maxTokens.max) {
        return { isValid: true, error: null };
      }
    }
    return { isValid: false, error: VALIDATION_RULES.maxTokens.errorMessage };
  }
  
  if (num < VALIDATION_RULES.maxTokens.min || num > VALIDATION_RULES.maxTokens.max) {
    return { isValid: false, error: VALIDATION_RULES.maxTokens.errorMessage };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate stop sequences array
 * @param {any} value - The value to validate (should be an array)
 * @returns {{ isValid: boolean, error: string | null }}
 */
export function validateStopSequences(value) {
  if (!Array.isArray(value)) {
    return { isValid: false, error: 'Stop sequences must be an array' };
  }
  
  if (value.length > VALIDATION_RULES.stopSequences.maxItems) {
    return { isValid: false, error: VALIDATION_RULES.stopSequences.errorMessage };
  }
  
  for (const seq of value) {
    if (typeof seq !== 'string') {
      return { isValid: false, error: 'Each stop sequence must be a string' };
    }
    if (seq.length > VALIDATION_RULES.stopSequences.maxItemLength) {
      return { isValid: false, error: VALIDATION_RULES.stopSequences.errorMessage };
    }
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate prompt name
 * @param {any} value - The value to validate
 * @returns {{ isValid: boolean, error: string | null }}
 */
export function validateName(value) {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return { isValid: false, error: VALIDATION_RULES.name.errorMessage };
  }
  
  if (value.length > VALIDATION_RULES.name.maxLength) {
    return { isValid: false, error: VALIDATION_RULES.name.errorMessage };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate prompt template
 * @param {any} value - The value to validate
 * @returns {{ isValid: boolean, error: string | null }}
 */
export function validateTemplate(value) {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return { isValid: false, error: VALIDATION_RULES.template.errorMessage };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validate entire form data
 * @param {object} formData - The form data to validate
 * @returns {{ isValid: boolean, errors: object }}
 * 
 * _Requirements: 9.1_
 */
export function validateForm(formData) {
  const errors = {};
  let isValid = true;
  
  // Validate name
  const nameResult = validateName(formData.name);
  if (!nameResult.isValid) {
    errors.name = nameResult.error;
    isValid = false;
  }
  
  // Validate template
  const templateText = formData.templateConfiguration?.text?.text || '';
  const templateResult = validateTemplate(templateText);
  if (!templateResult.isValid) {
    errors.template = templateResult.error;
    isValid = false;
  }
  
  // Validate inference configuration
  const inferenceConfig = formData.inferenceConfiguration?.text || {};
  
  // Validate temperature
  if (inferenceConfig.temperature !== undefined) {
    const tempResult = validateTemperature(inferenceConfig.temperature);
    if (!tempResult.isValid) {
      errors.temperature = tempResult.error;
      isValid = false;
    }
  }
  
  // Validate topP
  if (inferenceConfig.topP !== undefined) {
    const topPResult = validateTopP(inferenceConfig.topP);
    if (!topPResult.isValid) {
      errors.topP = topPResult.error;
      isValid = false;
    }
  }
  
  // Validate maxTokens
  if (inferenceConfig.maxTokens !== undefined) {
    const maxTokensResult = validateMaxTokens(inferenceConfig.maxTokens);
    if (!maxTokensResult.isValid) {
      errors.maxTokens = maxTokensResult.error;
      isValid = false;
    }
  }
  
  // Validate stopSequences
  if (inferenceConfig.stopSequences !== undefined) {
    const stopSeqResult = validateStopSequences(inferenceConfig.stopSequences);
    if (!stopSeqResult.isValid) {
      errors.stopSequences = stopSeqResult.error;
      isValid = false;
    }
  }
  
  return { isValid, errors };
}

/**
 * Validate a numeric value is within a range [min, max]
 * Generic function for property-based testing
 * @param {any} value - The value to validate
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {boolean}
 */
export function validateNumericRange(value, min, max) {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
}

/**
 * Validate a value is a positive integer
 * Generic function for property-based testing
 * @param {any} value - The value to validate
 * @returns {boolean}
 */
export function validatePositiveInteger(value) {
  if (typeof value === 'string') {
    // Allow string integers
    if (/^\d+$/.test(value.trim())) {
      return parseInt(value.trim(), 10) > 0;
    }
    return false;
  }
  
  if (typeof value !== 'number') return false;
  if (!Number.isInteger(value)) return false;
  return value > 0;
}
